import { serialize } from "cookie";
import { useDispatch, useSelector } from "react-redux";

import * as LTE from "#/def/lookup-tables.enums.gen";
import { SifNivoDostopa } from "#/def/lookup-tables.ident.gen";
import env from "#/env";
import useConnectionState from "#/hooks/useConnectionState";
import { useLookupTableList } from "#/hooks/useLookupTables";
import SPECIES from "#/model/SpeciesCodeList";
import { setAuthorization } from "#/redux/resource/CurrentAuthorizationSlice";
import * as scope from "#/utils/SpeciesScope";
import {
  getCurrentModule,
  getRedirectModuleUrl,
  hasModuleAccess,
  redirectToModule,
} from "#/utils/moduleAccess";
import { clearAuthIdCookie, setAuthIdCookie } from "#/utils/auth";

// fixed IDs for VF and BF authorizations
const VF_ID = "00000000-0a0a-0b0b-0c0c-000000000000";
const BF_ID = "00000000-fafa-fbfb-fcfc-000000000000";
const KL_ID = "00000000-ffff-fbfb-ffff-000000000000";
const KIS_ID = "00000000-ffff-fcfc-ffff-000000000000";

/**
 * Authoriziation label formatter.
 * @return string
 */
export function formatter(auth, levels = []) {
  const { nivoDostopa, subjekt } = auth || {};
  const level = levels.find((l) => l.sifra === nivoDostopa);

  let rval = level?.naziv?.sl || nivoDostopa || "";

  const short = [
    LTE.SifNivoDostopa.VF,
    LTE.SifNivoDostopa.BF,
    LTE.SifNivoDostopa.KL,
    LTE.SifNivoDostopa.KIS,
    LTE.SifNivoDostopa.ADMIN,
    LTE.SifNivoDostopa.RAZVIJALEC,
  ];

  if (!short.includes(nivoDostopa)) {
    // Do not include subjekt.naziv for BF, VF & KL:
    if (subjekt?.naziv) {
      // For other levels, include subjekt.naziv if available
      rval += ` (${subjekt.naziv})`;
    }
  }

  return rval;
}

/**
 * Hook to handle authorization changes.
 */
export default function useAuthorization({ load = true } = {}) {
  const dispatch = useDispatch();
  const { isOnline } = useConnectionState();

  // get translated level names from lookup table
  const levels = useLookupTableList(SifNivoDostopa);

  const set = (id) => {
    dispatch(setAuthorization(id));
  };

  const remove = () => {
    clearAuthIdCookie();
    dispatch(setAuthorization(null));
  };

  const PRESET_AUTHORIZATION_URL_PARAM = "preset-authorization";

  const setOrRedirect = (auth) => {
    const { nivoDostopa, id } = auth;
    // lets make sure that people see what they are intended to see
    // (1) if VF/KL is picked on pigs or small-livestock we will redirect user to horses
    // (2) if BF is picked on horses we will redirect user to small-livestock
    const onHorses = scope.has(scope.HORSES);
    const onlyHorses = [LTE.SifNivoDostopa.VF, LTE.SifNivoDostopa.KL, LTE.SifNivoDostopa.VF_ANALITIK];
    const onlyPigsAndSmallLivestock = [LTE.SifNivoDostopa.BF, LTE.SifNivoDostopa.BF_ANALITIK];

    let dst = null;

    // (3) KIS only ever works with cattle breeding values, which now live in
    // their own app. Picking it inside an animal app used to swap that app's
    // shell for a stripped cattle view; send the user to the cattle app instead.
    if (!env.isCattle && nivoDostopa === LTE.SifNivoDostopa.KIS) {
      dst = new URL(env.APP_URL_GOVEDO_PV);
    }

    if (!dst && onHorses && onlyPigsAndSmallLivestock.includes(nivoDostopa)) {
      // user picked authorization role that does not belong to horses,
      // we will redirect them to small-livestock
      dst = new URL(env.APP_URL_SMALL_LIVESTOCK);
    }

    if (!dst && !onHorses && onlyHorses.includes(nivoDostopa)) {
      // user picked authorization role that belongs to horses, but they are not on horses app,
      // we will redirect them to horses
      dst = new URL(env.APP_URL_HORSES);
    }

    if (!dst && nivoDostopa === LTE.SifNivoDostopa.REJEC) {
      const currentModule = getCurrentModule();
      if (currentModule && !hasModuleAccess(auth, currentModule)) {
        const redirectUrl = getRedirectModuleUrl(auth, currentModule);
        if (redirectUrl) {
          dst = new URL(redirectUrl);
        }
      }
    }

    if (dst) {
      if (!isOnline) {
        // Cross-app navigation is not possible while offline; stay on current app.
        set(id);
        return;
      }
      redirectToModule(dst.href, id, PRESET_AUTHORIZATION_URL_PARAM);
      return;
    }

    // all good, just set the authorization
    set(id);
    setAuthIdCookie(id)
  };

  const { current, isLoading, available } = useSelector(
    (state) => state.currentAuthorization
  );

  const full = () => available.find((auth) => auth.id === current) || null;
  const label = () => formatter(full(), levels);
  const level = () => full()?.nivoDostopa || null;

  /**
   * Authorization label formatter.
   */

  return {
    PRESET_AUTHORIZATION_URL_PARAM,

    // current authorization state
    current,
    isLoading,
    available,

    // get translated level names from lookup table
    levels,

    get VF() {
      return VF_ID;
    },
    get BF() {
      return BF_ID;
    },
    get KIS() {
      return KIS_ID;
    },

    get presets() {
      return {
        VF: VF_ID,
        BF: BF_ID,
        KL: KL_ID,
        KIS: KIS_ID,
        BF_ANALITIK: BF_ID,
        VF_ANALITIK: VF_ID,
      };
    },

    // full authorization object (from current id)
    get full() {
      return full();
    },

    get label() {
      return label();
    },

    get level() {
      return level();
    },

    get isAuthorized() {
      return !!level;
    },

    get isVF() {
      return level() === LTE.SifNivoDostopa.VF;
    },

    get isAnaliticVF() {
      return level() === LTE.SifNivoDostopa.VF_ANALITIK;
    },

    get isBF() {
      return level() === LTE.SifNivoDostopa.BF;
    },

    get isAnaliticBF() {
      return level() === LTE.SifNivoDostopa.BF_ANALITIK;
    },

    get isSelector() {
      return level() === LTE.SifNivoDostopa.SELEKCIONIST;
    },

    get isControlor() {
      return level() === LTE.SifNivoDostopa.KONTROLOR;
    },

    get isBreeder() {
      return level() === LTE.SifNivoDostopa.REJEC;
    },

    get isVeterinarian() {
      return level() === LTE.SifNivoDostopa.VETERINAR;
    },

    get isAdmin() {
      return level() === LTE.SifNivoDostopa.ADMIN;
    },

    get isDeveloper() {
      return level() === LTE.SifNivoDostopa.RAZVIJALEC;
    },

    get isKL() {
      return level() === LTE.SifNivoDostopa.KL;
    },

    get isKIS() {
      return level() === LTE.SifNivoDostopa.KIS;
    },

    // form permissions
    get canCreate() {
      return !!full()?.allowCreate;
    },

    get canUpdate() {
      return !!full()?.allowUpdate;
    },

    get canDelete() {
      return !!full()?.allowDelete;
    },

    get canWrite() {
      return !!full()?.allowCreate && !!full()?.allowUpdate;
    },

    /**
     * Check if the current authorization has access to a specific species module.
     * Only restricts REJEC level; all staff roles return true.
     * @param {string} module - MODULE_PIGS | MODULE_HORSES | MODULE_SMALL_LIVESTOCK
     */
    hasModuleAccess: (module) => hasModuleAccess(full(), module),

    // set authorization
    set,

    // set authorization or redirect if not compatible with current species scope
    setOrRedirect,

    // remove current authorization
    remove,

    // format authorization label
    formatter: (auth) => formatter(auth, levels),
  };
}
